import { resolveActiveAuditOverrides } from './truthAttestations';
/**
 * R-78 — Post-render verifier: tier-1 provider facts must win over LLM/Tier-3 values.
 */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteRagReport } from '../../types/siteRag';
import type { CanonicalFactKey, CanonicalFactValue } from '../../types/auditCanonicalFacts';
import { extractCanonicalFacts } from './canonicalFactReconciler';
import { isUnavailableFloodZone, unavailableFloodZoneAction } from './floodZoneAvailability';

export type Tier1ProviderId =
  | 'NREL' | 'USGS' | 'EIA' | 'FEMA' | 'USFWS' | 'NRC'
  | 'PEERINGDB' | 'HIFLD' | 'WRI' | 'AIRNOW';

/** Read-only tier-1 provider snapshot (maps from SED / site evidence). */
export interface EvidenceCache {
  providerData: Partial<Record<CanonicalFactKey, {
    value: string | number;
    units?: string;
    tier1Source: Tier1ProviderId;
    provenance?: string;
    /**
     * Additional legitimate values for a fact whose real-world referent is
     * multi-level (2026-08-13, Redhead voltage RCA): a site can sit near a
     * 345 kV transmission tap AND a 230/69 kV dual-voltage substation, and
     * the intent's own utility string / phase notes declare the extra
     * levels. A mention matching ANY accepted value corroborates; a foreign
     * value (e.g. 138 kV nobody declared) still blocks. Intent-traceable
     * only — never populated from report prose.
     */
    altValues?: number[];
  }>>;
}

export interface PrecedenceViolation {
  /** An unavailable provider lookup requests source review, not replacement. */
  kind?: 'provider-unavailable';
  fact: CanonicalFactKey;
  reportedValue: string | number;
  tier1Value: string | number;
  tier1Source: Tier1ProviderId;
  severity: 'block' | 'warn';
  suggestedFix: string;
  excerpt?: string;
  /** Dot path into SED, e.g. `sed.siteProfile.data.acres`. */
  provenance?: string;
}

export class ProviderPrecedenceError extends Error {
  readonly violations: PrecedenceViolation[];
  /** Narrative-complete draft when the gate blocked persistence. */
  readonly draftReport?: SiteRagReport;

  constructor(violations: PrecedenceViolation[], draftReport?: SiteRagReport) {
    super(
      `Provider precedence violation: ${violations.map((v) => `${v.fact} report=${v.reportedValue} tier1=${v.tier1Value}`).join('; ')}`,
    );
    this.name = 'ProviderPrecedenceError';
    this.violations = violations;
    this.draftReport = draftReport;
  }
}

function inferProviderFromSource(source: string | undefined): Tier1ProviderId | null {
  const s = (source ?? '').toUpperCase();
  if (s.includes('HIFLD')) return 'HIFLD';
  if (s.includes('FEMA') || s.includes('NFHL')) return 'FEMA';
  if (s.includes('USFWS') || s.includes('IPAC')) return 'USFWS';
  if (s.includes('NRC') || s.includes('ADAMS')) return 'NRC';
  if (s.includes('PEERING') || s.includes('PEERINGDB')) return 'PEERINGDB';
  if (s.includes('NREL')) return 'NREL';
  if (s.includes('USGS')) return 'USGS';
  if (s.includes('EIA')) return 'EIA';
  if (s.includes('WRI') || s.includes('AQUEDUCT')) return 'WRI';
  if (s.includes('AIRNOW') || s.includes('EPA AQS')) return 'AIRNOW';
  return null;
}

/**
 * kV levels the USER's own intent declares — utility strings like
 * "1803 Electric Cooperative (Moss Bluff 230/69 kV)" and phase notes like
 * "69kV upgrade ($4-7M) in service". These are intent-traceable voltage
 * levels a report may legitimately cite alongside the provider's primary
 * (Redhead RCA: report cited the 69 kV low side, tier-1 held 230 → false
 * block on every regeneration). Slashed pairs yield BOTH members.
 */
export function intentDeclaredVoltagesKV(
  intent: import('../../types/siteRag').ProjectIntent | null | undefined,
): number[] {
  if (!intent) return [];
  // gridUtilityName is a GRID-SERVICE declaration by construction — every kV
  // in it is a grid voltage. Free-text phase NOTES are context-gated (Codex
  // review on #134): only a kV whose surrounding window reads grid-flavored
  // (and not equipment-flavored) widens the accepted set — a "13.8 kV
  // generator terminal" note must never launder an interconnect claim.
  // (The phase-notes INPUT was retired by the intent-input audit, 2026-09-21;
  // notes are still read here for legacy stored phases — this only ever WIDENS
  // the accepted set, so existing sites keep avoiding the Redhead false block.
  // New sites declare a grid voltage via `gridUtilityName`.)
  const gridTexts: string[] = [];
  const noteTexts: string[] = [];
  const pushCfg = (cfg: { gridUtilityName?: string } | undefined) => {
    if (cfg?.gridUtilityName) gridTexts.push(cfg.gridUtilityName);
  };
  pushCfg((intent as { powerSourceConfig?: { gridUtilityName?: string } }).powerSourceConfig);
  for (const p of intent.phases ?? []) {
    pushCfg((p as { powerSourceConfig?: { gridUtilityName?: string } }).powerSourceConfig);
    if (p.notes) noteTexts.push(p.notes);
  }
  const out = new Set<number>();
  const collect = (t: string, gate: ((window: string) => boolean) | null) => {
    for (const m of t.matchAll(/\b(\d{2,4}(?:\.\d+)?)\s*(?:\/\s*(\d{2,4}(?:\.\d+)?)\s*)?kV\b/gi)) {
      if (gate) {
        const idx = m.index ?? 0;
        const window = t.slice(Math.max(0, idx - 60), idx + m[0]!.length + 60);
        if (!gate(window)) continue;
      }
      out.add(parseFloat(m[1]!));
      if (m[2] !== undefined) out.add(parseFloat(m[2]!));
    }
  };
  const noteGate = (window: string): boolean =>
    /(grid|substation|interconnect|transmission|utility|feeder|line|service|upgrade)/i.test(window)
    && !/(generator|genset|recip|turbine|alternator|inverter|switchgear bus|terminal)/i.test(window);
  for (const t of gridTexts) collect(t, null);
  for (const t of noteTexts) collect(t, noteGate);
  return [...out].filter((v) => Number.isFinite(v) && v > 0);
}

/**
 * Recorded provider-precedence WAIVERS (2026-08-14) — the user's "keep the
 * KMZ / developer value" decisions, stored as legacy
 * `{ gate: 'provider-precedence', fields: [factKey], reportTypeId? }`
 * overrides. A waived fact must not raise R-78 anywhere: BOTH the user audit
 * (runUserAudit) and the publish gate (runFullAudit) apply this predicate, or
 * publish denies exactly what the audit cleared. A waiver scoped to a
 * reportTypeId applies to that type; an unscoped one applies site-wide.
 */
export function isPrecedenceWaived(
  auditOverrides: readonly unknown[] | undefined,
  reportType: string | undefined,
  fact: string,
): boolean {
  const active = resolveActiveAuditOverrides(auditOverrides as readonly import('../../types/site').SiteAuditOverride[] | undefined);
  return active.some((o) => {
    const legacy = o as { gate?: string; fields?: string[]; reportTypeId?: string };
    if (legacy?.gate !== 'provider-precedence') return false;
    if (!legacy.fields?.includes(fact)) return false;
    return !legacy.reportTypeId || legacy.reportTypeId === reportType;
  });
}

/** Build tier-1 fact cache from the latest SED provider sections.
 *  `opts.intent` widens multi-level facts (voltage) with intent-declared
 *  values — see `altValues` on the cache entry. */
export function buildEvidenceCacheFromSed(
  sed: SiteEvidenceDocument | null | undefined,
  opts?: { intent?: import('../../types/siteRag').ProjectIntent | null },
): EvidenceCache {
  const providerData: EvidenceCache['providerData'] = {};
  if (!sed) return { providerData };

  const grid = sed.grid?.data;
  const gridProvider = inferProviderFromSource(sed.grid?.source) ?? 'HIFLD';
  if (grid?.substationDistKm != null && Number.isFinite(grid.substationDistKm)) {
    providerData.interconnectionDistanceKm = {
      value: grid.substationDistKm,
      units: 'km',
      tier1Source: gridProvider,
      provenance: 'sed.grid.data.substationDistKm',
    };
  }
  if (grid?.substationName) {
    providerData.nearestSubstation = {
      value: grid.substationName,
      tier1Source: gridProvider,
      provenance: 'sed.grid.data.substationName',
    };
  }
  if (grid?.voltage) {
    const m = grid.voltage.match(/([\d.]+)\s*(?:kV)?/i);
    // Only emit a positive voltage — a "0 kV" SED placeholder is "unknown",
    // not a tier-1 precedence claim, so it must never enter providerData.
    const primary = m ? parseFloat(m[1]) : NaN;
    if (Number.isFinite(primary) && primary > 0) {
      // Multi-level acceptance (Redhead voltage RCA): every OTHER number in
      // the provider string ("230/69" holds both sides of a dual-voltage
      // substation) plus every intent-declared kV level. A mention matching
      // any of these corroborates; foreign voltages still block.
      const alt = new Set<number>();
      for (const n of grid.voltage.matchAll(/[\d.]+/g)) {
        const v = parseFloat(n[0]);
        if (Number.isFinite(v) && v > 0 && v !== primary) alt.add(v);
      }
      // Live intent when the caller has it; the SED's at-compile intent
      // otherwise — so every gate (post-gen stamp, user audit, sync) shares
      // the same acceptance without threading intent through each signature.
      for (const v of intentDeclaredVoltagesKV(opts?.intent ?? sed.fullProjectIntent)) {
        if (v !== primary) alt.add(v);
      }
      providerData.voltageKV = {
        value: primary,
        units: 'kV',
        tier1Source: gridProvider,
        provenance: 'sed.grid.data.voltage',
        ...(alt.size ? { altValues: [...alt] } : {}),
      };
    }
  }
  if (grid?.isoRegion) {
    providerData.gridOperator = {
      value: grid.isoRegion,
      tier1Source: gridProvider,
      provenance: 'sed.grid.data.isoRegion',
    };
  }

  const flood = sed.flood?.data;
  const floodProvider = inferProviderFromSource(sed.flood?.source) ?? 'FEMA';
  if (flood && typeof flood.floodZone === 'string') {
    providerData.floodZone = {
      value: flood.floodZone.toUpperCase(),
      tier1Source: floodProvider,
      provenance: 'sed.flood.data.floodZone',
    };
  }

  const acres = sed.siteProfile?.data?.acres;
  const siteProvider = inferProviderFromSource(sed.siteProfile?.source) ?? 'USGS';
  if (acres != null && Number.isFinite(acres) && acres > 0) {
    providerData.acreage = {
      value: acres,
      units: 'acres',
      tier1Source: siteProvider,
      provenance: 'sed.siteProfile.data.acres',
    };
  }

  const intentMw = sed.fullProjectIntent?.powerRequirementMW
    ?? sed.projectIntent?.powerRequirementMW;
  if (intentMw != null && intentMw > 0) {
    providerData.capacityMW = {
      value: intentMw,
      units: 'MW',
      tier1Source: 'EIA',
      provenance: 'sed.fullProjectIntent.powerRequirementMW',
    };
  }

  const nrc = sed.nuclearInfra?.data;
  const nrcProvider = inferProviderFromSource(sed.nuclearInfra?.source) ?? 'NRC';
  if (
    nrc?.nearestReactorDistKm != null &&
    Number.isFinite(nrc.nearestReactorDistKm) &&
    nrc.nearestReactorDistKm < 900 &&
    !/^none\b/i.test(String(nrc.nearestReactorName ?? '').trim())
  ) {
    providerData.nuclearProximityKm = {
      value: nrc.nearestReactorDistKm,
      units: 'km',
      tier1Source: nrcProvider,
      provenance: 'sed.nuclearInfra.data.nearestReactorDistKm',
    };
  }

  const ix = sed.connectivity?.data;
  const ixProvider = inferProviderFromSource(sed.connectivity?.source) ?? 'PEERINGDB';
  if (ix?.nearestIxDistKm != null && Number.isFinite(ix.nearestIxDistKm)) {
    providerData.interconnectionDistanceKm ??= {
      value: ix.nearestIxDistKm,
      units: 'km',
      tier1Source: ixProvider,
      provenance: 'sed.connectivity.data.nearestIxDistKm',
    };
  }

  return { providerData };
}

function numericAgree(key: CanonicalFactKey, a: number, b: number): boolean {
  switch (key) {
    case 'capacityMW':
      return Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1) <= 0.01;
    case 'interconnectionDistanceKm':
    case 'nuclearProximityKm':
      return Math.abs(a - b) <= 0.5;
    case 'voltageKV':
      return Math.abs(a - b) / Math.max(a, b, 1) <= 0.02;
    case 'acreage':
      return Math.abs(a - b) / Math.max(a, b, 1) <= 0.05;
    default:
      return a === b;
  }
}

function normalizeFloodZoneValue(raw: string | number): string {
  return String(raw)
    .trim()
    .toUpperCase()
    .replace(/^(?:FEMA\s+)?(?:100[- ]YR\s+)?(?:FLOOD\s+)?(?:ZONE\s+)?/, '')
    .trim();
}

export function valuesAgree(key: CanonicalFactKey, reported: string | number, tier1: string | number): boolean {
  if (typeof reported === 'number' && typeof tier1 === 'number') {
    return numericAgree(key, reported, tier1);
  }
  if (key === 'floodZone') {
    return normalizeFloodZoneValue(reported) === normalizeFloodZoneValue(tier1);
  }
  return String(reported).trim().toUpperCase() === String(tier1).trim().toUpperCase();
}

function narrativeReportView(report: SiteRagReport): SiteRagReport {
  return { ...report, datasets: undefined };
}

// Facts where multiple legitimate values commonly co-exist in prose — a phase
// capacity alongside the full build-out, nearby transmission voltages alongside
// the interconnect voltage, a buildable footprint alongside the total parcel.
// ONLY these use BEST-MATCH (corroborated if any mention agrees) + a zero/
// sentinel guard ("0 MW of spare headroom" is not a claim). Single-valued facts
// (interconnection distance, flood zone, substation, grid operator) keep the
// STRICT per-mention check below, so an injected wrong value still blocks even
// when the correct value also appears elsewhere (e.g. echoed in a data table).
const MULTI_VALUE_KEYS = new Set<CanonicalFactKey>(['capacityMW', 'voltageKV', 'acreage']);
// acreage tier-1 is a polygon-derived geometry ESTIMATE (frequently even
// mis-attributed to a provider) and routinely differs from the buildable
// footprint cited in prose. A mismatch is a WARNING, never a hard block.
const WARN_ONLY_KEYS = new Set<CanonicalFactKey>(['acreage']);

/** Of several disagreeing occurrences, the one nearest the tier-1 value
 *  (so the surfaced violation points at the most defensible drift, not a
 *  random mention). For non-numeric facts, the first occurrence. */
function pickClosest(
  candidates: CanonicalFactValue[],
  tier1Value: string | number,
): CanonicalFactValue {
  if (typeof tier1Value !== 'number') return candidates[0]!;
  let best = candidates[0]!;
  let bestDiff = Number.POSITIVE_INFINITY;
  for (const c of candidates) {
    if (typeof c.value !== 'number') continue;
    const diff = Math.abs(c.value - tier1Value);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = c;
    }
  }
  return best;
}

/** Compare narrative-extracted facts against tier-1 provider cache.
 *
 * Uses BEST-MATCH semantics: the report corroborates a tier-1 fact if ANY
 * prose mention agrees with it, so legitimately co-existing values — a phase
 * capacity alongside the full build-out, nearby transmission voltages
 * alongside the interconnect voltage, a buildable footprint alongside the
 * total parcel — do NOT false-positive. A violation is raised only when NO
 * mention agrees (genuine drift/hallucination), and at most one per fact
 * (the closest occurrence). Sentinel/zero magnitudes are excluded entirely. */
export function verifyProviderPrecedence(
  report: SiteRagReport,
  evidence: EvidenceCache,
): PrecedenceViolation[] {
  const facts = extractCanonicalFacts(narrativeReportView(report));
  const violations: PrecedenceViolation[] = [];

  for (const [key, tier1] of Object.entries(evidence.providerData) as [
    CanonicalFactKey,
    NonNullable<EvidenceCache['providerData'][CanonicalFactKey]>,
  ][]) {
    const reportedList = facts[key];
    if (!reportedList?.length) continue;

    // Zero/sentinel guard: a 0 / non-finite tier-1 magnitude means "unknown",
    // never a precedence claim. Without this, an unpopulated provider field
    // (e.g. "voltageKV report=69 tier1=0") would falsely block a correct
    // report. Covers BOTH the strict single-value branch and MULTI_VALUE below.
    if (typeof tier1.value === 'number' && (!Number.isFinite(tier1.value) || tier1.value <= 0)) continue;

    if (key === 'floodZone' && isUnavailableFloodZone(tier1.value)) {
      for (const rep of reportedList) {
        violations.push({
          kind: 'provider-unavailable', fact: key, reportedValue: rep.value,
          tier1Value: tier1.value, tier1Source: tier1.tier1Source, severity: 'warn',
          suggestedFix: unavailableFloodZoneAction(rep.value, tier1.tier1Source),
          excerpt: rep.excerpt, provenance: tier1.provenance,
        });
      }
      continue;
    }

    // Single-valued facts: exactly one true value, so any disagreeing mention is
    // genuine drift — keep the strict per-occurrence check (an injected wrong
    // distance/zone must still block even if the correct value is echoed too).
    if (!MULTI_VALUE_KEYS.has(key)) {
      for (const rep of reportedList) {
        if (valuesAgree(key, rep.value, tier1.value)) continue;
        violations.push({
          fact: key,
          reportedValue: rep.value,
          tier1Value: tier1.value,
          tier1Source: tier1.tier1Source,
          severity: 'block',
          suggestedFix: `Replace ${rep.value} with tier-1 ${tier1.value} from [PROVIDER:${tier1.tier1Source}] (${tier1.provenance ?? key}).`,
          excerpt: rep.excerpt,
          provenance: tier1.provenance,
        });
      }
      continue;
    }

    // Multi-valued fact: drop sentinel/zero magnitudes, then corroborate by
    // BEST-MATCH — if ANY mention agrees with tier-1 the fact is fine (phase vs
    // total, nearby vs interconnect, buildable vs total all legitimately co-exist).
    const candidates = reportedList.filter(
      (r) => typeof r.value !== 'number' || (Number.isFinite(r.value) && r.value > 0),
    );
    if (!candidates.length) continue;
    // Multi-level acceptance: the tier-1 primary OR any intent/provider-
    // declared alternate corroborates (dual-voltage substations, nearby
    // transmission levels). altValues is intent-traceable by construction.
    const accepted: (string | number)[] = [tier1.value, ...(tier1.altValues ?? [])];
    if (candidates.some((r) => accepted.some((a) => valuesAgree(key, r.value, a)))) continue;

    // None agree → genuine drift: flag the single closest occurrence.
    const closest = pickClosest(candidates, tier1.value);
    const severity: PrecedenceViolation['severity'] = WARN_ONLY_KEYS.has(key)
      ? 'warn'
      : 'block';
    violations.push({
      fact: key,
      reportedValue: closest.value,
      tier1Value: tier1.value,
      tier1Source: tier1.tier1Source,
      severity,
      suggestedFix: `Replace ${closest.value} with tier-1 ${tier1.value} from [PROVIDER:${tier1.tier1Source}] (${tier1.provenance ?? key}).`,
      excerpt: closest.excerpt,
      provenance: tier1.provenance,
    });
  }

  return violations;
}

export type PrecedenceFixMode = 'flagOnly' | 'autocorrect';

/** Replace inline tier-3 values with tier-1 values + footnote (markdown / prose). */
export function applyPrecedenceFixes(
  markdown: string,
  violations: PrecedenceViolation[],
  mode: PrecedenceFixMode = 'flagOnly',
): string {
  if (mode !== 'autocorrect' || violations.length === 0) return markdown;
  let out = markdown;
  for (const v of violations) {
    // Covers current source-gap warnings and legacy stamped UNKNOWN mismatches.
    if (v.kind === 'provider-unavailable' || (v.fact === 'floodZone' && isUnavailableFloodZone(v.tier1Value))) continue;
    const from = String(v.reportedValue);
    const to = String(v.tier1Value);
    if (!from || from === to) continue;
    const footnote = `(corrected from prior value via ${v.tier1Source} precedence)`;
    out = out.split(from).join(`${to} ${footnote}`);
  }
  return out;
}

function walkStringFields(
  obj: unknown,
  fn: (path: string, value: string) => string,
  path = '',
): unknown {
  if (typeof obj === 'string') return fn(path, obj);
  if (Array.isArray(obj)) {
    return obj.map((item, i) => walkStringFields(item, fn, `${path}[${i}]`));
  }
  if (obj && typeof obj === 'object') {
    if (path.includes('datasets')) return obj;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      out[k] = walkStringFields(v, fn, path ? `${path}.${k}` : k);
    }
    return out;
  }
  return obj;
}

/** Apply autocorrect to all narrative string fields on a report. */
export function applyPrecedenceFixesToReport(
  report: SiteRagReport,
  violations: PrecedenceViolation[],
  mode: PrecedenceFixMode = 'flagOnly',
): SiteRagReport {
  if (mode !== 'autocorrect' || violations.length === 0) return report;
  const fixed = walkStringFields(report, (path, text) => {
    if (path.includes('references') || path.includes('metadata')) return text;
    return applyPrecedenceFixes(text, violations, 'autocorrect');
  }) as SiteRagReport;
  const md = (report as SiteRagReport & { markdown?: string }).markdown;
  if (typeof md === 'string') {
    (fixed as SiteRagReport & { markdown?: string }).markdown = applyPrecedenceFixes(
      md,
      violations,
      'autocorrect',
    );
  }
  return fixed;
}

/**
 * Run verifier + optional autocorrect; DETECT-AND-RECORD-ONLY.
 *
 * R-78 is no longer a publish gate. This function NEVER throws and NEVER
 * withholds a report: it stamps any mismatches onto
 * `report.qaFindings.providerPrecedenceViolations` (the audit feed consumed by
 * runUserAudit → runPrecedenceChecks → SiteAuditTab, review-only) and returns
 * the report so it persists normally. The `throwOnBlock` option is retained for
 * call-site back-compat but is now ignored — there is no throw path.
 */
export function applyProviderPrecedenceGate(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  opts: {
    autocorrect?: boolean;
    /** @deprecated R-78 never throws/blocks — kept only for call-site compat. */
    throwOnBlock?: boolean;
    /** Intent for multi-level fact acceptance (voltage) — see `altValues`. */
    intent?: import('../../types/siteRag').ProjectIntent | null;
  } = {},
): SiteRagReport {
  const evidence = buildEvidenceCacheFromSed(sed, { intent: opts.intent });
  const violations = verifyProviderPrecedence(report, evidence);
  if (violations.length === 0) {
    // ZOMBIE-STAMP FIX (2026-08-13, Redhead "is 2" RCA): an update-mode
    // draft INHERITS the parent's qaFindings, and this early return left an
    // inherited providerPrecedenceViolations stamp untouched — so a false
    // positive stamped once (by a since-fixed extractor) propagated through
    // EVERY regeneration and re-blocked the audit forever, even though the
    // fresh body produced zero violations. A clean pass must clear the
    // inherited stamp, not preserve it.
    const inherited = report.qaFindings?.providerPrecedenceViolations;
    if (inherited?.length) {
      return {
        ...report,
        qaFindings: { ...report.qaFindings, providerPrecedenceViolations: [] },
      };
    }
    return report;
  }

  const mode: PrecedenceFixMode = opts.autocorrect ? 'autocorrect' : 'flagOnly';
  let out = applyPrecedenceFixesToReport(report, violations, mode);

  const blocks = violations.filter((v) => v.severity === 'block');

  // RECORD-ONLY: always stamp, never throw. The audit section surfaces these.
  out = {
    ...out,
    qaFindings: {
      ...(out.qaFindings ?? {}),
      providerPrecedenceViolations: violations,
    },
  };
  console.warn('[R-78 precedence] Provider precedence violations (recorded for audit, non-blocking):', {
    reportId: out.id,
    blocks: blocks.length,
    warns: violations.filter((v) => v.severity === 'warn').length,
    facts: violations.map((v) => v.fact),
  });

  return out;
}
